import { RouteRecordRaw } from 'vue-router';
import AppLayout from 'src/core/components/layouts/AppLayout.vue';
import { UserServerResponse } from 'src/core/types/user';
import { ROLE_IDS } from 'src/modules/userAccess/types';
import { hasEudrInAccess } from 'src/core/composables/useUserAccess';

// custom meta interface
interface CustomRouteMeta {
  title?: string;
  parent_route?: string;
  layout?: any;
  permission_to_access?: string;
  permission_to_access_fnc?: (
    user: UserServerResponse,
    checkAccess: (permission: string) => boolean
  ) => boolean;
  disabled?: boolean;
  breadcrumb?: { name: string }[];
}

// a custom route type that extends the original vue-router RouteRecordRaw
type AppRouteRecordRaw = Omit<RouteRecordRaw, 'meta'> & {
  meta?: CustomRouteMeta;
};

/**
 * Array of route objects.
 * Each route object represents a route in the application.
 * @type {AppRouteRecordRaw[]}
 */
const routes: AppRouteRecordRaw[] = [
  {
    name: 'datasets',
    path: '/',
    component: () => import('src/modules/dataset/pages/DatasetListPage.vue'),
    meta: {
      title: 'Datasets',
      parent_route: 'datasets',
      layout: AppLayout,
      permission_to_access: 'app.dataset.api:read',
    },
  },
  {
    name: 'vault',
    path: '/vault',
    component: () => import('src/modules/vault/pages/VaultListPage.vue'),
    meta: {
      title: 'vault',
      parent_route: 'datasets',
      layout: AppLayout,
      permission_to_access_fnc: (user, checkAccess) =>
        checkAccess('app.vault.api:read_vault_parcel') &&
        hasEudrInAccess(user?.access),
    },
  },
  {
    name: 'vaultAuditTrail',
    path: '/vault/audit',
    component: () => import('src/modules/vault/pages/VaultAuditTrail.vue'),
    meta: {
      title: 'Audit Trail',
      parent_route: 'vault',
      breadcrumb: [{ name: '/' }, { name: '/vault' }, { name: '/vault/audit' }],
      layout: AppLayout,
      permission_to_access: 'app.vault.api:read_vault_audit',
    },
  },
  {
    name: 'parcels',
    path: '/parcels',
    component: () => import('src/modules/parcel/pages/ParcelListPage.vue'),
    meta: {
      title: 'Parcels & DDS',
      parent_route: 'parcels',
      breadcrumb: [{ name: '/' }, { name: '/parcels' }],
      layout: AppLayout,
      permission_to_access_fnc: (user, checkAccess) =>
        checkAccess('app.parcel.api:read') && hasEudrInAccess(user?.access),
    },
  },
  {
    name: 'parcelCreate',
    path: '/parcels/create',
    component: () => import('src/modules/parcel/pages/ParcelCreatePage.vue'),
    meta: {
      title: 'Create',
      parent_route: 'parcels',
      breadcrumb: [
        { name: '/' },
        { name: '/parcels' },
        { name: '/parcels/create' },
      ],
      layout: AppLayout,
      permission_to_access: 'app.parcel.api:create',
    },
  },
  {
    name: 'auditTrail',
    path: '/parcels/audit/:id?',
    component: () => import('src/modules/parcel/pages/ParcelAuditTrail.vue'),
    meta: {
      title: 'Audit Trail',
      parent_route: 'parcels',
      breadcrumb: [
        { name: '/' },
        { name: '/parcels' },
        { name: '/parcels/audit/:id?' },
      ],
      layout: AppLayout,
      permission_to_access: 'app.parcel.api:read_parcel_audit',
    },
  },
  {
    name: 'parcelEdit',
    path: '/parcels/edit/:id',
    component: () => import('src/modules/parcel/pages/ParcelCreatePage.vue'),
    meta: {
      title: 'Edit',
      parent_route: 'parcels',
      breadcrumb: [
        { name: '/' },
        { name: '/parcels' },
        { name: '/parcels/edit/:id' },
      ],
      layout: AppLayout,
      permission_to_access: 'app.parcel.api:create',
    },
  },
  {
    name: 'userAccess',
    path: '/users',
    component: () =>
      import('src/modules/userAccess/pages/AccessRightsListPage.vue'),
    meta: {
      title: 'Users',
      permission_to_access: 'app.user.api:create',
      breadcrumb: [{ name: '/' }, { name: '/users' }],
      layout: AppLayout,
    },
  },
  {
    name: 'suppliers',
    path: '/suppliers',
    component: () =>
      import('src/modules/userAccess/pages/SuppliersListPage.vue'),
    meta: {
      title: 'Suppliers',
      permission_to_access: 'app.supplier.api:read',
      breadcrumb: [{ name: '/' }, { name: '/suppliers' }],
      layout: AppLayout,
    },
  },
  {
    name: 'customers',
    path: '/customers',
    component: () =>
      import('src/modules/userAccess/pages/CustomersListPage.vue'),
    meta: {
      title: 'Customers',
      permission_to_access: 'app.user.api:read_customers',
      breadcrumb: [{ name: '/' }, { name: '/customers' }],
      layout: AppLayout,
    },
  },
  {
    name: 'customersAuditTrail',
    path: '/customers/audit',
    component: () =>
      import('src/modules/userAccess/pages/AuditTrail.vue'),
    meta: {
      title: 'Audit trail',
      parent_route: 'customers',
      permission_to_access: 'app.user.api:read_customers',
      breadcrumb: [
        { name: '/' },
        { name: '/customers' },
        { name: '/customers/audit' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'apiAccess',
    path: '/api_access',
    component: () => import('src/core/pages/ApiAccessPage.vue'),
    meta: {
      title: 'Api Access',
      parent_route: 'apiAccess',
      permission_to_access_fnc: (user, checkAccess) => {
        if (!checkAccess('app.user.api:create_app_token')) return false;
        const customer = user.customer ?? user.supplier?.customer;
        return customer?.enterprise_bundle !== false;
      },
      breadcrumb: [{ name: '/' }, { name: '/api_access' }],
      layout: AppLayout,
    },
  },
  {
    name: 'userSettings',
    path: '/settings',
    component: () =>
      import('src/modules/userAccess/pages/UserSettingsPage.vue'),
    meta: {
      title: 'Account Settings',
      parent_route: 'settings',
      permission_to_access: 'app.user.api:read',
      breadcrumb: [{ name: '/' }, { name: '/settings' }],
      layout: AppLayout,
    },
  },
  {
    name: 'datasetEdit',
    path: '/dataset/edit/:id',
    component: () => import('src/modules/dataset/pages/DatasetEditPage.vue'),
    meta: {
      title: 'Edit',
      parent_route: 'datasets',
      permission_to_access: 'app.dataset.api:update',
      breadcrumb: [{ name: '/' }, { name: '/dataset/edit/:id' }],
      layout: AppLayout,
    },
  },
  {
    name: 'results',
    path: '/dataset/result/:id?',
    component: () => import('src/modules/result/pages/ResultPage.vue'),
    meta: {
      title: 'Results',
      parent_route: 'results',
      permission_to_access: 'app.dataset.api:read', // need permission to access
      breadcrumb: [{ name: '/' }, { name: '/dataset/result/:id?' }],
      layout: AppLayout,
    },
  },
  {
    name: 'farmPlots',
    path: '/farmplot',
    component: () => import('src/modules/farm_plots/pages/FarmPlots.vue'),
    meta: {
      title: 'Farm Plots',
      parent_route: 'results',
      permission_to_access: 'app.dataset.api:read', // need permission to access
      breadcrumb: [{ name: '/' }, { name: '/farmplot/:id?' }],
      layout: AppLayout,
    },
  },
  {
    name: 'farmPlotsAuditTrail',
    path: '/farmplot/audit',
    component: () => import('src/modules/farm_plots/pages/AuditTrail.vue'),
    meta: {
      title: 'Audit Trail',
      parent_route: 'farmPlots',
      permission_to_access:
        'app.reclassification.api:read_reclassification_audit',
      breadcrumb: [
        { name: '/' },
        { name: '/farmplot' },
        { name: '/farmplot/audit' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'datasetCreate',
    path: '/dataset/create',
    component: () => import('src/modules/dataset/pages/DatasetCreatePage.vue'),
    meta: {
      title: 'Create',
      parent_route: 'datasets',
      permission_to_access: 'app.dataset.api:create',
      breadcrumb: [{ name: '/' }, { name: '/dataset/create' }],
      layout: AppLayout,
    },
  },
  {
    name: 'suppliersCreate',
    path: '/suppliers/create',
    component: () =>
      import('src/modules/userAccess/pages/SuppliersCreatePage.vue'),
    meta: {
      title: 'Add Supplier',
      permission_to_access: 'app.supplier.api:read',
      breadcrumb: [
        { name: '/' },
        { name: '/suppliers' },
        { name: '/suppliers/create' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'suppliersEdit',
    path: '/suppliers/edit/:id',
    component: () =>
      import('src/modules/userAccess/pages/SuppliersEditPage.vue'),
    meta: {
      title: 'Edit supplier',
      permission_to_access: 'app.supplier.api:read',
      breadcrumb: [
        { name: '/' },
        { name: '/suppliers' },
        { name: '/suppliers/edit/:id' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'customersView',
    path: '/customers/view/:id',
    component: () =>
      import('src/modules/userAccess/pages/CustomersViewPage.vue'),
    meta: {
      title: 'View Customer',
      permission_to_access: 'app.user.api:read_customers',
      breadcrumb: [
        { name: '/' },
        { name: '/customers' },
        { name: '/customers/view/:id' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'usersEdit',
    path: '/users/edit/:id',
    component: () =>
      import('src/modules/userAccess/pages/AccessRightsEditPage.vue'),
    meta: {
      title: 'Edit',
      parent_route: 'userAccess',
      permission_to_access: 'app.user.api:update', // need permission to access
      breadcrumb: [
        { name: '/' },
        { name: '/users' },
        { name: '/users/edit/:id' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'usersClone',
    path: '/users/clone/:id',
    component: () =>
      import('src/modules/userAccess/pages/AccessRightsClonePage.vue'),
    meta: {
      title: 'Clone',
      parent_route: 'userAccess',
      permission_to_access: 'app.user.api:create', // need permission to access
      breadcrumb: [
        { name: '/' },
        { name: '/users' },
        { name: '/users/clone/:id' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'usersCreate',
    path: '/users/create',
    component: () =>
      import('src/modules/userAccess/pages/AccessRightsCreatePage.vue'),
    meta: {
      title: 'Add user',
      parent_route: 'userAccess',
      permission_to_access: 'app.user.api:create', // need permission to access
      breadcrumb: [
        { name: '/' },
        { name: '/users' },
        { name: '/users/create' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'usersCreateSupplier',
    path: '/users/create/supplier',
    component: () =>
      import('src/modules/userAccess/pages/SupplierCreatorAddPage.vue'),
    meta: {
      title: 'Add user to supplier',
      parent_route: 'userAccess',
      permission_to_access: 'app.user.api:create',
      breadcrumb: [
        { name: '/' },
        { name: '/users' },
        { name: '/users/create/supplier' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'datasources',
    path: '/datasource/read',
    component: () => import('src/modules/datasource/pages/ListPage.vue'),
    meta: {
      title: 'Datasources',
      parent_route: 'datasources',
      permission_to_access: 'app.datasource_page.api:read', // need permission to access
      disabled: true,
      breadcrumb: [{ name: '/' }, { name: '/datasource/read' }],
      layout: AppLayout,
    },
  },
  {
    name: 'datasourceCreate',
    path: '/datasource/create',
    component: () => import('src/modules/datasource/pages/CreatePage.vue'),
    meta: {
      title: 'Create',
      parent_route: 'datasources',
      permission_to_access: 'app.datasource_page.api:create', // need permission to access
      disabled: true,
      breadcrumb: [
        { name: '/' },
        { name: '/datasource/read' },
        { name: '/datasource/create' },
      ],
      layout: AppLayout,
    },
  },
  {
    name: 'Tests & Resolutions',
    path: '/tests',
    component: () => import('src/modules/test/pages/TestsAndResolutions.vue'),
    meta: {
      title: 'Tests & Resolutions',
      parent_route: 'Tests & Resolutions',
      permission_to_access: 'app.user.api:read',
      disabled: false,
      breadcrumb: [{ name: '/' }, { name: '/tests' }],
      layout: AppLayout,
    },
  },
  {
    name: 'usageAnalytics',
    path: '/usage/analytics',
    component: () => import('src/modules/usage/pages/UsageAnalyticsPage.vue'),
    meta: {
      title: 'Usage Analytics',
      parent_route: 'usageAnalytics',
      permission_to_access_fnc: (user, checkAccess) => {
        if (checkAccess('app.customer.api:usage_summary') && !user.supplier) {
          return true;
        }
      },
      breadcrumb: [{ name: '/' }, { name: '/usage/analytics' }],
      layout: AppLayout,
    },
  },
  {
    name: 'diagnostic',
    path: '/internal/reflayer',
    component: () => import('src/modules/admin/page/DiagnosticEngPage.vue'),
    meta: {
      title: 'Diagnostic',
      parent_route: 'diagnostic',
      layout: AppLayout,
      permission_to_access: 'app.user.api:read_customers', //reused meridia admin
    },
  },
  {
    name: 'diagnostic-v2',
    path: '/internal/reflayer/v2',
    component: () => import('src/modules/admin/page/DiagnosticV2Page.vue'),
    meta: {
      title: 'Diagnostic V2',
      parent_route: 'diagnostic-v2',
      layout: AppLayout,
      permission_to_access: 'app.user.api:read_customers', //reused meridia admin
    },
  },
  {
    name: 'login',
    path: '/login',
    component: () => import('src/modules/login/pages/LoginPage.vue'),
    meta: {
      title: 'Login',
      layout: '',
    },
  },
  {
    name: 'ssoRedirect',
    path: '/sso-redirect',
    component: () => import('src/modules/login/pages/SsoRedirectPage.vue'),
    meta: {
      title: 'SSO Redirect',
      layout: '',
    },
  },
  {
    name: 'userInviteVerify',
    path: '/users/invite/verify',
    component: () => import('src/modules/userAccess/pages/VerifyInvite.vue'),
  },
  {
    name: 'forgotPassword',
    path: '/user/forgot_password',
    component: () => import('src/modules/login/pages/ForgotPassword.vue'),
  },
  {
    name: 'resetPassword',
    path: '/user/reset_password',
    component: () => import('src/modules/login/pages/ResetPassword.vue'),
  },
  {
    name: 'noPermissions',
    path: '/noPermissions',
    component: () => import('src/core/pages/NoPermissionsPage.vue'),
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    name: 'notFound',
    path: '/notFound',
    component: () => import('src/core/pages/ErrorNotFound.vue'),
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('src/core/pages/ErrorNotFound.vue'),
  },
];

export default routes;
